import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, message, Modal, Tabs, Input } from 'antd';
import { CheckOutlined, StopOutlined, SearchOutlined } from '@ant-design/icons';
import { userService } from '../../services/api/user.service';
import { User } from '../../types/user';

const { TabPane } = Tabs;
const { Search } = Input;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAll();
      setUsers(response.data);
    } catch (error) {
      message.error('Greška pri učitavanju korisnika');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (user: User) => {
    try {
      await userService.approve(user.id);
      message.success('Korisnik uspješno odobren');
      fetchUsers();
      setApprovalModalVisible(false);
    } catch (error) {
      message.error('Greška pri odobravanju korisnika');
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.water_meter?.meter_id.toLowerCase().includes(searchLower);
    
    switch (activeTab) {
      case 'pending':
        return !user.is_approved && matchesSearch;
      case 'approved':
        return user.is_approved && matchesSearch;
      default:
        return matchesSearch;
    }
  });

  const columns = [
    {
      title: 'Ime i prezime',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: User, b: User) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Broj vodomjera',
      dataIndex: ['water_meter', 'meter_id'],
      key: 'water_meter_id',
    },
    {
      title: 'Adresa',
      dataIndex: ['water_meter', 'address'],
      key: 'address',
    },
    {
      title: 'Status',
      key: 'status',
      render: (text: string, record: User) => (
        <Tag color={record.is_approved ? 'green' : 'orange'}>
          {record.is_approved ? 'Odobren' : 'Čeka odobrenje'}
        </Tag>
      )
    
    },
    {
      title: 'Akcije',
      key: 'action',
      render: (text: string, record: User) => (
        <Space size="middle">
          {!record.is_approved && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => {
                setSelectedUser(record);
                setApprovalModalVisible(true);
              }}
            >
              Odobri
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="Upravljanje korisnicima" className="mb-4">
        <div className="mb-4">
          <Search
            placeholder="Pretraga korisnika..."
            allowClear
            enterButton
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Čekaju odobrenje" key="pending">
            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loading}
            />
          </TabPane>
          <TabPane tab="Odobreni korisnici" key="approved">
            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loading}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Odobrenje korisnika"
        open={approvalModalVisible}
        onOk={() => {
          if (selectedUser) {
            handleApprove(selectedUser);
          }
        }}
        onCancel={() => setApprovalModalVisible(false)}
        okText="Odobri"
        cancelText="Odustani"
      >
        <p>Da li ste sigurni da želite odobriti korisnika {selectedUser?.name}?</p>
        <div className="mt-4">
          <p><strong>Email:</strong> {selectedUser?.email}</p>
          <p><strong>Telefon:</strong> {selectedUser?.phone}</p>
          <p><strong>Broj vodomjera:</strong> {selectedUser?.water_meter?.meter_id}</p>
          <p><strong>Adresa:</strong> {selectedUser?.water_meter?.address}</p>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;